import {useEffect, useRef, useMemo, useState} from 'react'
import * as Comlink from 'comlink'
import _ from 'lodash'
import {getMockupWorker} from '../webworkers/helpers/customShape'
import {useSelectedVariantContext} from '../components/step2/context/SelectedVariantContext'
import {handleCaptureArtworkWithFilter} from '../components/step2/context/SelectedVariantContext/helper'
import useEditorSafeZone from '../hooks/useEditorSafeZone'
import {hasFeatureTag} from '../helpers/settings'

const EXPORT_DIR = 'Mockup-Custom_Shape_2-side_Printed_Wood_Bookshelf_Sign_1773726720'
const ASSET_BASE_URL = `http://localhost:9001/${EXPORT_DIR}/layers`
const CANVAS_DIMENSION = {
    WIDTH: 1000,
    HEIGHT: 1000,
}

/**
 * Hook to manage Pixi custom shape preview rendering
 *
 * @param {Object} options
 * @param {'canvas' | 'image'} options.outputMode
 * @param {Object} options.background
 * @param {string} options.background.src
 * @returns {{
        pixiCanvasRef: React.RefObject<HTMLCanvasElement | null>,
        isCustomShape: boolean,
        image: HTMLImageElement | null,
        isReady: boolean,
        isUpdating: boolean,
    }}
 */
export function usePreviewCustomShape(
    /** @type {Object} */
    options = {},
) {
    const {outputMode = 'canvas', background} = options || {}
    const {
        state: {selectedSide, loadingImageLayer, selectedAttribute, designs, pickerAttributes, productCreatedType},
    } = useSelectedVariantContext()

    const pixiCanvasRef = useRef(null)
    const workerApiRef = useRef(null)
    const tmpCanvasRef = useRef(null)

    const [image, setImage] = useState(null)
    const [printSide, setPrintSide] = useState(null)
    const [isReady, setIsReady] = useState(false)
    const [isUpdating, setIsUpdating] = useState(false)

    const {safeZone: cutSide} = useEditorSafeZone()

    /**
     * NOTE: Only working if the store has "custom_shape_step2" feature tag and the product mockup type is "custom_shape"
     */
    const isCustomShape = productCreatedType === 'custom_shape' && hasFeatureTag('custom_shape_step2')

    const documentJson = useMemo(() => {
        if (!selectedAttribute || !selectedSide) return null
        return _.chain(selectedAttribute)
            .get(['mockup', 'meta', 'mockup_infos'])
            .find((i) => i.side === selectedSide)
            .value()
    }, [selectedAttribute, selectedSide])
console.log(documentJson, selectedSide)
    const updateRef = useRef()

    useEffect(() => {
        updateRef.current = async () => {
            if (selectedSide === 'All' || selectedSide === 'Safezone') return
            if (loadingImageLayer) return
            if (!workerApiRef.current || !documentJson) return

            try {
                setIsUpdating(true)
                let hasUpdates = false

                if (cutSide?.src) {
                    await workerApiRef.current.updateCutSide(cutSide.src)
                    hasUpdates = true
                }
                if (printSide?.src) {
                    await workerApiRef.current.updateFrontSide(printSide.src)
                    hasUpdates = true
                }
                if (background?.src) {
                    await workerApiRef.current.updateBackground(background.src)
                    hasUpdates = true
                }

                if (!hasUpdates) {
                    await workerApiRef.current.render()
                }

                if (outputMode === 'image') {
                    const bitmap = await workerApiRef.current.getBitmap()
                    if (bitmap) {
                        const offscreenCanvas = new OffscreenCanvas(bitmap.width, bitmap.height)
                        const ctx = offscreenCanvas.getContext('2d')
                        ctx.drawImage(bitmap, 0, 0)
                        const blob = await offscreenCanvas.convertToBlob({type: 'image/png'})

                        const reader = new FileReader()
                        reader.onloadend = () => setImage(reader.result)
                        reader.readAsDataURL(blob)
                    }
                }
            } catch (error) {
                console.error('[Pixi Worker] Error within update:', error)
            } finally {
                setIsUpdating(false)
            }
        }
    })

    // Memoize the debounced execution function so it persists across renders
    const debounceUpdate = useMemo(
        () =>
            _.debounce(() => {
                if (updateRef.current) updateRef.current()
            }, 500),
        [],
    )

    /**
     * Initialize Pixi worker and setup canvas
     */
    useEffect(() => {
        if (!isCustomShape || !documentJson) return
        if (outputMode === 'canvas' && !pixiCanvasRef.current) return

        let isMounted = true

        async function initPixiWorker() {
            try {
                const api = await getMockupWorker()
                if (!isMounted) return
                workerApiRef.current = api

                let width = CANVAS_DIMENSION.WIDTH
                let height = CANVAS_DIMENSION.HEIGHT
                let offscreen = null

                if (outputMode === 'canvas' && pixiCanvasRef.current) {
                    const canvas = pixiCanvasRef.current
                    const container = canvas.parentElement

                    const containerBbox = container?.getBoundingClientRect()
                    if (containerBbox) {
                        width = Math.round(containerBbox.width || CANVAS_DIMENSION.WIDTH)
                        height = Math.round(containerBbox.height || CANVAS_DIMENSION.HEIGHT)
                    }

                    canvas.width = width
                    canvas.height = height

                    offscreen = canvas.transferControlToOffscreen()
                    await api.init(Comlink.transfer(offscreen, [offscreen]), width, height)
                } else {
                    await api.init(null, width, height)
                }

                await api.loadDocument({
                    documentJson,
                    baseUrl: ASSET_BASE_URL,
                })

                if (isMounted) {
                    setIsReady(true)
                    debounceUpdate()
                }
            } catch (err) {
                console.error('[Pixi Worker] Error:', err)
            }
        }

        initPixiWorker()

        // Cleanup
        return () => {
            isMounted = false
            setIsReady(false)
            if (workerApiRef.current) {
                workerApiRef.current.dispose()
                workerApiRef.current = null
            }
            setImage(null)
            debounceUpdate.cancel()
        }
    }, [isCustomShape, documentJson, debounceUpdate])

    /**
     * Setup resize observer for responsive canvas
     */
    useEffect(() => {
        if (!isCustomShape) return
        if (outputMode !== 'canvas') return

        const container = pixiCanvasRef.current?.parentElement
        if (!container) return

        let timeoutId = null
        const observer = new ResizeObserver((entries) => {
            if (!entries[0] || !workerApiRef.current) return

            const {width, height} = entries[0].contentRect
            if (width && height) {
                clearTimeout(timeoutId)
                const timeoutCallback = () =>
                    workerApiRef.current?.resize({
                        width: Math.round(width),
                        height: Math.round(height),
                    })
                timeoutId = setTimeout(timeoutCallback, 150)
            }
        })

        observer.observe(container)
        return () => {
            observer.disconnect()
            clearTimeout(timeoutId)
        }
    }, [isCustomShape])

    /**
     * Trigger update when dependencies change
     */
    useEffect(() => {
        if (!isCustomShape) return

        debounceUpdate()

        return () => {
            debounceUpdate.cancel()
        }
    }, [isCustomShape, loadingImageLayer, cutSide?.src, printSide?.src, debounceUpdate])

    /**
     * Effect to capture print-side artwork automatically
     */
    useEffect(() => {
        if (!isCustomShape || loadingImageLayer) return
        if (selectedSide === 'All' || selectedSide === 'Safezone') return

        const capturePrintSide = () => {
            const attribute = pickerAttributes.find((attr) => attr._id === selectedAttribute._id)
            if (!attribute) return

            const stageRef = attribute.ref[selectedSide]
            const safeZoneData = attribute.safeZone[selectedSide]

            if (!stageRef || !safeZoneData) return

            const canvas = handleCaptureArtworkWithFilter(
                {
                    stageRef,
                    safeZone: safeZoneData,
                },
                {
                    excludeByName: ['background'],
                    pixelRatio: 1,
                },
            )

            if (canvas) {
                if (!tmpCanvasRef.current) {
                    tmpCanvasRef.current = document.createElement('canvas')
                }
                const tmpCanvas = tmpCanvasRef.current

                if (tmpCanvas.width !== canvas.width || tmpCanvas.height !== canvas.height) {
                    tmpCanvas.width = canvas.width
                    tmpCanvas.height = canvas.height
                }

                const ctx = tmpCanvas.getContext('2d')

                ctx.clearRect(0, 0, canvas.width, canvas.height)
                ctx.drawImage(canvas, 0, 0)

                setPrintSide({
                    src: tmpCanvas.toDataURL('image/png'),
                    width: canvas.width,
                    height: canvas.height,
                })
            } else {
                setPrintSide(null)
            }
        }

        const timeoutId = setTimeout(capturePrintSide, 300)
        return () => clearTimeout(timeoutId)
    }, [isCustomShape, pickerAttributes, designs, selectedAttribute, loadingImageLayer, selectedSide])

    return {
        pixiCanvasRef,
        isCustomShape,
        image,
        isReady,
        isUpdating,
    }
}
